import { PRIORITY_META, STATUS_META, TYPE_META, avatarColor, initials, readableOn } from '@/lib/design';
import type { ItemStatus, ItemType, Priority, UserSummary } from '@/types/api';

/** Circulo con iniciales. El color lo fija el id, no la posicion en la lista. */
export function Avatar({
  user,
  size = 24,
  ring = false,
  className = '',
}: {
  user: UserSummary | null | undefined;
  size?: number;
  /** Borde blanco para apilar avatares superpuestos. */
  ring?: boolean;
  className?: string;
}) {
  const label = user?.fullName ?? 'Sin asignar';
  const background = user ? avatarColor(user.id) : '#3a3d5e';

  return (
    <span
      title={label}
      aria-label={label}
      className={`flex flex-none items-center justify-center rounded-full font-semibold ${
        ring ? 'border-2 border-surface' : ''
      } ${className}`}
      style={{
        width: size,
        height: size,
        background,
        color: readableOn(background),
        fontSize: Math.round(size * 0.38),
        letterSpacing: '.02em',
      }}
    >
      {user?.avatarUrl ? (
        <img src={user.avatarUrl} alt="" className="size-full rounded-full object-cover" />
      ) : (
        initials(label)
      )}
    </span>
  );
}

/** Avatares superpuestos con un "+N" cuando el equipo no cabe. */
export function AvatarStack({
  users,
  max = 4,
  size = 26,
}: {
  users: UserSummary[];
  max?: number;
  size?: number;
}) {
  const shown = users.slice(0, max);
  const rest = users.length - shown.length;

  return (
    <div className="flex items-center">
      {shown.map((user) => (
        <span key={user.id} className="-mr-1.5 last:mr-0">
          <Avatar user={user} size={size} ring />
        </span>
      ))}
      {rest > 0 && (
        <span
          className="flex flex-none items-center justify-center rounded-full border-2 border-surface bg-muted-surface font-semibold text-muted"
          style={{ width: size, height: size, fontSize: Math.round(size * 0.36) }}
        >
          +{rest}
        </span>
      )}
    </div>
  );
}

export function TypeBadge({ type }: { type: ItemType }) {
  const meta = TYPE_META[type];
  return (
    <span
      className="rounded-badge border px-1.5 py-px text-[10px] font-bold tracking-[.06em]"
      style={{ background: meta.bg, color: meta.fg, borderColor: meta.border }}
    >
      {meta.label}
    </span>
  );
}

export function StatusBadge({ status }: { status: ItemStatus }) {
  const meta = STATUS_META[status];
  return (
    <span
      className="whitespace-nowrap rounded-md border px-2 py-0.5 text-[11px] font-semibold"
      style={{
        color: meta.color,
        // El fondo y el borde son el mismo color a muy baja opacidad, para que
        // la insignia se lea como una version suave del punto de la columna.
        background: `${meta.color}14`,
        borderColor: `${meta.color}33`,
      }}
    >
      {meta.label}
    </span>
  );
}

/** Prioridad del ticket. `dot` la reduce a un punto para las tarjetas apretadas. */
export function PriorityBadge({ priority, dot = false }: { priority: Priority; dot?: boolean }) {
  const meta = PRIORITY_META[priority];

  if (dot) {
    return (
      <span
        title={`Prioridad ${meta.label.toLowerCase()}`}
        aria-label={`Prioridad ${meta.label.toLowerCase()}`}
        className="size-2 flex-none rounded-full"
        style={{ background: meta.color }}
      />
    );
  }

  return (
    <span
      className="whitespace-nowrap rounded-badge border px-1.5 py-px text-[10px] font-bold tracking-[.06em]"
      style={{ background: meta.soft, color: meta.color, borderColor: meta.line }}
    >
      {meta.label.toUpperCase()}
    </span>
  );
}

export function BlockedBadge() {
  return (
    <span className="rounded-badge border border-danger-line bg-danger-soft px-1.5 py-px text-[10px] font-bold tracking-[.05em] text-danger">
      BLOQUEADA
    </span>
  );
}

/** Punto de color que encabeza cada columna del tablero. */
export function StatusDot({ status, size = 8 }: { status: ItemStatus; size?: number }) {
  return (
    <span
      aria-hidden="true"
      className="flex-none rounded-full"
      style={{ width: size, height: size, background: STATUS_META[status].color }}
    />
  );
}

export function Spinner({ label = 'Cargando' }: { label?: string }) {
  return (
    <div
      role="status"
      aria-label={label}
      className="size-7 animate-spin rounded-full border-2 border-line border-t-brand"
    />
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="rounded-panel border border-dashed border-line-dashed px-6 py-10 text-center">
      <p className="text-[13.5px] font-medium text-subtle">{title}</p>
      {hint && <p className="mt-1 text-xs text-faint">{hint}</p>}
    </div>
  );
}

export function ErrorNotice({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="alert"
      className="rounded-control border border-danger-line bg-danger-soft px-3 py-2 text-[13px] text-danger"
    >
      {children}
    </div>
  );
}
